import React, { useId, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import {
    Box,
    Button,
    Buttonbar,
    ButtonbarButton,
    ButtonbarButtonLabel,
    Divider,
    FlexBox,
    FluidImage,
    Icon,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogNav,
    SquareImageContainer,
    TextInput,
    tokens,
} from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { View } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { useBriefContext } from '../../../../BriefContext';
import { WorkEntity } from './ReferenceProjectsReview';
import { useCopyWorksMutation, useExpertStudioUrlQuery } from './referenceProjects.generated';

export function WorkEntityViewsModal({
    setModalOpen,
    isModalOpen,
    workEntity,
}: {
    setModalOpen: (isOpen: boolean) => void;
    isModalOpen: boolean;
    workEntity: WorkEntity;
}) {
    return (
        <ModalDialog
            isOpen={isModalOpen}
            onRequestDismiss={() => {
                setModalOpen(false);
            }}
            takeOver
            onlyRenderWhenOpen
        >
            <ModalContent workEntity={workEntity} />
        </ModalDialog>
    );
}

function ModalContent({ workEntity }: { workEntity: WorkEntity }) {
    const [selectedViewIndex, setSelectedViewIndex] = useState<number>(0);
    const selectedView = workEntity.views[selectedViewIndex];
    const [copyWorks, { loading }] = useCopyWorksMutation();
    const { isInternalUser, auth } = useIdentityContext();

    const { correlationId } = useBriefContext();

    const { data: studioUrlData } = useExpertStudioUrlQuery({
        variables: { collaborationPublicId: correlationId || '' },
        skip: !correlationId,
    });

    let footerAction: React.ReactNode;
    if (workEntity.hasCopyPermission) {
        const handleCopyClick = async () => {
            if (!correlationId) return;

            try {
                const result = await copyWorks({
                    variables: {
                        input: {
                            collaborationPublicId: correlationId,
                            workIds: [workEntity.workId],
                        },
                    },
                });

                // Navigate to expert studio with the newly copied work id
                const newWorkId = result.data?.copyWorks.newWorkIds[0];
                if (newWorkId) {
                    const baseStudioUrl = studioUrlData?.getCollaboration.collaboration?.studioUrl;
                    if (baseStudioUrl) {
                        const url = new URL(baseStudioUrl);
                        url.searchParams.set('workId', newWorkId);
                        window.location.href = url.href;
                    }
                }
            } catch (error) {
                Bugsnag.notify(error as Error, (event) => {
                    event.addMetadata('copyWorks', {
                        workIds: workEntity.workId,
                        collaborationPublicId: correlationId,
                    });
                });
            }
        };

        footerAction = (
            <Button iconPosition="left" onClick={handleCopyClick} loading={loading}>
                <Icon iconType="duplicate" />
                {__('Make a copy & edit', {
                    _context:
                        'Primary button label in the reference project views modal to duplicate a project and open it for editing',
                })}
            </Button>
        );
    }
    const userIsInternal = auth !== undefined && isInternalUser(auth);
    if (userIsInternal) {
        footerAction = <CopyProjectIdAction projectId={workEntity.workId} />;
    }

    const id = useId();

    return (
        <ModalDialogContent pb={0}>
            <FlexBox
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'space-between'}
                style={{
                    height: '100%',
                }}
            >
                <Box>
                    <ModalDialogNav>
                        <ModalDialogCloseButton
                            accessibleText={__('Close', {
                                _context:
                                    'Accessible text for the close button on the reference project views modal',
                            })}
                        />
                    </ModalDialogNav>
                    <ModalDialogBody>
                        <FlexBox
                            display={'flex'}
                            flexDirection={'column'}
                            alignItems={'center'}
                            gap={'to-actions'}
                            mb={8}
                        >
                            <ViewsButtonBar
                                views={workEntity.views}
                                onClick={(index) => setSelectedViewIndex(index)}
                                selectedValue={selectedViewIndex}
                                ariaControls={`active-work-entity-${id}`}
                            />
                            {selectedView && (
                                <SquareImageContainer
                                    style={{ maxWidth: '640px' }}
                                    id={`active-work-entity-${id}`}
                                >
                                    <FluidImage
                                        src={selectedView._links.image.href}
                                        alt={selectedView._links.image.title}
                                    />
                                </SquareImageContainer>
                            )}
                        </FlexBox>
                    </ModalDialogBody>
                </Box>
                {footerAction && <ModalFooter>{footerAction}</ModalFooter>}
            </FlexBox>
        </ModalDialogContent>
    );
}

function CopyProjectIdAction({ projectId }: { projectId: string }) {
    const [copied, setCopied] = useState(false);

    return (
        <FlexBox gap="4" alignItems="flex-start">
            <TextInput value={projectId} readOnly disabled style={{ minWidth: 300 }} />
            <Button
                iconPosition="right"
                onClick={() => {
                    window.navigator.clipboard.writeText(projectId);
                    setCopied(true);
                }}
                style={{ minWidth: 160 }}
                disabled={copied}
            >
                {copied ? 'Copied!' : 'Copy project ID'}
                <Icon iconType={copied ? 'check' : 'copy'} />
            </Button>
        </FlexBox>
    );
}

function ModalFooter({ children }: { children: React.ReactNode }) {
    return (
        <ModalDialogFooter
            p={6}
            style={{
                position: 'sticky',
                bottom: 0,
                background: tokens.SwanSemColorBgStandard,
                zIndex: 100,
            }}
        >
            <Divider />
            <FlexBox display={'flex'} flexDirection={'column'} alignItems={'center'} pt={6}>
                {children}
            </FlexBox>
        </ModalDialogFooter>
    );
}

function ViewsButtonBar({
    onClick,
    selectedValue,
    views,
    ariaControls,
}: {
    onClick: (selected: number) => void;
    selectedValue: number;
    views: (View | null)[];
    ariaControls?: string;
}) {
    return (
        <Buttonbar
            variant="single-select"
            selectedValue={selectedValue.toString()}
            aria-controls={ariaControls}
        >
            {views.map((view, index) =>
                view ? (
                    <ButtonbarButton
                        key={view.name}
                        value={index.toString()}
                        onClick={() => onClick(index)}
                    >
                        <ButtonbarButtonLabel>{view._links.image.title}</ButtonbarButtonLabel>
                    </ButtonbarButton>
                ) : null
            )}
        </Buttonbar>
    );
}
